import React from "react";
import { Link } from "react-router-dom";
import {
  FiCheckCircle,
  FiClock,
  FiDollarSign,
  FiHome,
  FiPieChart,
  FiTrendingDown,
  FiTrendingUp,
  FiUsers,
} from "react-icons/fi";
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { formatBdt, formatChangePct } from "../../utils/leadershipRoles";

/**
 * One renderer per dashboard section. The keys are the ones GET /api/leadership/overview returns in
 * `visible` (see backend/app/Support/LeadershipSections.php); the page renders a section only when the
 * API lists it, so a role never gets a card it is not allowed to see.
 */

const COLORS = ["#10b981", "#3b82f6", "#8b5cf6", "#f59e0b", "#ef4444", "#06b6d4"];

const none = (value) => value === null || value === undefined;

/* ---------------------------------------------------------------- KPI cards */

const Kpi = ({ title, value, note, trend, icon: Icon, color }) => (
  <div className="ld-kpi-card" data-testid="ld-kpi">
    <div className="ld-kpi-icon" style={{ backgroundColor: `${color}15`, color }}>
      <Icon size={26} />
    </div>
    <div className="ld-kpi-body">
      <div className="ld-kpi-title">{title}</div>
      <div className="ld-kpi-value">{value}</div>
      <div className={`ld-kpi-note ${trend || ""}`}>
        {trend === "up" && <FiTrendingUp />}
        {trend === "down" && <FiTrendingDown />}
        <span>{note}</span>
      </div>
    </div>
  </div>
);

const changeTrend = (pct) => (none(pct) ? undefined : Number(pct) >= 0 ? "up" : "down");

const KPI_RENDERERS = {
  revenue_ytd: (d) => (
    <Kpi
      key="revenue_ytd"
      title="Revenue YTD"
      value={formatBdt(d.amount, { compact: true })}
      note={`${formatChangePct(d.change_pct)} vs last year`}
      trend={changeTrend(d.change_pct)}
      icon={FiDollarSign}
      color="#10b981"
    />
  ),
  revenue_month: (d) => (
    <Kpi
      key="revenue_month"
      title="Collected This Month"
      value={formatBdt(d.amount, { compact: true })}
      note={`${formatChangePct(d.change_pct)} vs last month`}
      trend={changeTrend(d.change_pct)}
      icon={FiTrendingUp}
      color="#3b82f6"
    />
  ),
  avg_deal_size: (d) => (
    <Kpi
      key="avg_deal_size"
      title="Avg Deal Size"
      value={formatBdt(d.amount, { compact: true })}
      note="approved bookings"
      icon={FiPieChart}
      color="#8b5cf6"
    />
  ),
  sales_staff: (d) => (
    <Kpi
      key="sales_staff"
      title="Sales Staff"
      value={String(d.count ?? 0)}
      note="Marketing Officers & Managers"
      icon={FiUsers}
      color="#8b5cf6"
    />
  ),
  active_properties: (d) => (
    <Kpi
      key="active_properties"
      title="Active Properties"
      value={String(d.count ?? 0)}
      note="in inventory"
      icon={FiHome}
      color="#3b82f6"
    />
  ),
  collection: (d) => (
    <Kpi
      key="collection"
      title="Collection Rate"
      value={none(d.rate) ? "—" : `${d.rate}%`}
      note={
        none(d.rate)
          ? "no installments due this month"
          : `${formatBdt(d.collected, { compact: true })} of ${formatBdt(d.expected, { compact: true })} due this month`
      }
      icon={FiCheckCircle}
      color="#f59e0b"
    />
  ),
  pending_deals: (d) => (
    <Kpi
      key="pending_deals"
      title="Pending Deals"
      value={String(d.count ?? 0)}
      note="awaiting approval"
      icon={FiClock}
      color="#ef4444"
    />
  ),
  investment_summary: (d) => (
    <React.Fragment key="investment_summary">
      <Kpi
        title="Total Invested"
        value={formatBdt(d.total_invested, { compact: true })}
        note="across your bookings"
        icon={FiDollarSign}
        color="#3b82f6"
      />
      <Kpi
        title="Active Holdings"
        value={`${d.active_count ?? 0} Bookings`}
        note="booked or sold"
        icon={FiPieChart}
        color="#10b981"
      />
      <Kpi
        title="Upcoming Due"
        value={formatBdt(d.upcoming_due, { compact: true })}
        note="still to pay"
        icon={FiClock}
        color="#f59e0b"
      />
    </React.Fragment>
  ),
};

export const KPI_KEYS = Object.keys(KPI_RENDERERS);

export const renderKpi = (key, data) => KPI_RENDERERS[key](data || {});

/* ---------------------------------------------------------------- panels */

const Panel = ({ title, wide, children, action }) => (
  <div className={`ld-panel${wide ? " ld-panel-wide" : ""}`} data-testid="ld-panel">
    {action ? (
      <div className="ld-panel-head">
        <h2>{title}</h2>
        {action}
      </div>
    ) : (
      <h2>{title}</h2>
    )}
    {children}
  </div>
);

const Empty = ({ children }) => <p className="ld-empty">{children}</p>;

const Avatar = ({ name }) => <div className="ld-avatar">{(name || "?").substring(0, 2).toUpperCase()}</div>;

const MonthlyRevenue = ({ data, months, onMonthsChange }) => {
  const series = (data?.series || []).map((row) => ({
    month: row.label,
    revenue: Number(((Number(row.amount) || 0) / 1_000_000).toFixed(2)),
  }));

  return (
    <Panel
      title="Monthly Revenue"
      action={
        <select
          className="ld-select"
          aria-label="Months shown"
          value={months}
          onChange={(e) => onMonthsChange(Number(e.target.value))}
        >
          <option value={3}>Last 3 months</option>
          <option value={6}>Last 6 months</option>
          <option value={12}>Last 12 months</option>
        </select>
      }
    >
      {series.length === 0 ? (
        <Empty>No revenue data yet.</Empty>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={series}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="month" stroke="#718096" />
            <YAxis stroke="#718096" />
            <Tooltip formatter={(v) => [`৳${v}M`, "Revenue"]} />
            <Bar dataKey="revenue" fill="#10b981" name="Revenue (M৳)" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      )}
    </Panel>
  );
};

const QuarterlyTrends = ({ data }) => {
  const rows = (data || []).map((row) => ({
    quarter: row.label,
    revenue: Number(((Number(row.revenue) || 0) / 1_000_000).toFixed(2)),
    deals: row.deals,
  }));

  return (
    <Panel title="Long-Term Performance Trends" wide>
      {rows.length === 0 ? (
        <Empty>No quarterly data yet.</Empty>
      ) : (
        <ResponsiveContainer width="100%" height={360}>
          <ComposedChart data={rows}>
            <defs>
              <linearGradient id="ldRevenueGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
            <XAxis dataKey="quarter" stroke="#6b7280" />
            <YAxis stroke="#6b7280" />
            <Tooltip />
            <Legend />
            <Area
              type="monotone"
              dataKey="revenue"
              fill="url(#ldRevenueGradient)"
              stroke="#10b981"
              strokeWidth={3}
              name="Revenue (M৳)"
            />
            <Bar dataKey="deals" fill="#3b82f6" name="Deals" />
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </Panel>
  );
};

const PieCard = ({ title, rows, emptyText, label }) => (
  <Panel title={title}>
    {rows.length === 0 ? (
      <Empty>{emptyText}</Empty>
    ) : (
      <ResponsiveContainer width="100%" height={300}>
        <PieChart>
          <Pie data={rows} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={95} label={label}>
            {rows.map((_, index) => (
              <Cell key={index} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    )}
  </Panel>
);

const SalesByArea = ({ data }) => (
  <PieCard
    title="Sales by Area"
    emptyText="No area sales yet."
    rows={(data || []).map((row) => ({ name: row.area, value: Number(row.share_pct) || 0 }))}
    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
  />
);

const InventoryByType = ({ data }) => (
  <PieCard
    title="Inventory by Property Type"
    emptyText="No inventory data yet."
    rows={(data || []).map((row) => ({ name: row.type, value: row.total }))}
    label={({ name, value }) => `${name}: ${value}`}
  />
);

const Bar100 = ({ label, pct, color }) => (
  <div className="ld-bar-item">
    <div className="ld-bar-info">
      <span>{label}</span>
      <span>{pct ?? 0}%</span>
    </div>
    <div className="ld-bar-track">
      <div className="ld-bar-fill" style={{ width: `${Math.min(100, pct ?? 0)}%`, background: color }} />
    </div>
  </div>
);

const InventoryStatus = ({ data }) => {
  const inventory = data?.inventory || {};
  const week = data?.booking_week_change;

  return (
    <Panel title="Inventory Status">
      <Bar100 label="Sold Units" pct={inventory.sold_pct} color="#10b981" />
      <Bar100 label="Booked" pct={inventory.booked_pct} color="#f59e0b" />
      <Bar100 label="Available" pct={inventory.available_pct} color="#ef4444" />
      {week && week.last_week > 0 && !none(week.change_pct) && (
        <div className="ld-insight">
          <strong>Insight:</strong> Bookings this week are {formatChangePct(week.change_pct)} vs last week (
          {week.this_week} vs {week.last_week}).
        </div>
      )}
    </Panel>
  );
};

const RevenueByProperty = ({ data }) => {
  const rows = data || [];
  const max = Math.max(1, ...rows.map((r) => Number(r.revenue) || 0));

  return (
    <Panel title="Revenue by Property">
      {rows.length === 0 ? (
        <Empty>No property revenue yet.</Empty>
      ) : (
        rows.map((row) => (
          <div className="ld-bar-item" key={row.property_id}>
            <div className="ld-bar-info">
              <span>{row.property_name}</span>
              <span>
                {formatBdt(row.revenue, { compact: true })} ({row.share_pct}%)
              </span>
            </div>
            <div className="ld-bar-track">
              <div
                className="ld-bar-fill"
                style={{ width: `${Math.round(((Number(row.revenue) || 0) / max) * 100)}%` }}
              />
            </div>
          </div>
        ))
      )}
    </Panel>
  );
};

const TopAgents = ({ data }) => (
  <Panel title="Top Performing Agents">
    {(data || []).length === 0 ? (
      <Empty>No agent sales recorded yet.</Empty>
    ) : (
      data.map((agent) => (
        <div className="ld-row" key={agent.user_id}>
          <div className="ld-row-main">
            <Avatar name={agent.name} />
            <div>
              <div className="ld-row-title">{agent.name}</div>
              <div className="ld-row-sub">{agent.deals} deals closed</div>
            </div>
          </div>
          <div className="ld-row-value">{formatBdt(agent.volume, { compact: true })}</div>
        </div>
      ))
    )}
  </Panel>
);

const Commission = ({ data }) => (
  <Panel title="Commission Totals">
    <div className="ld-row">
      <div>
        <div className="ld-row-title">This Month</div>
        <div className="ld-row-sub">Company commission earnings</div>
      </div>
      <div className="ld-row-value">{formatBdt(data?.this_month, { compact: true })}</div>
    </div>
    <div className="ld-row">
      <div>
        <div className="ld-row-title">Last Month</div>
        <div className="ld-row-sub">Company commission earnings</div>
      </div>
      <div className="ld-row-value">{formatBdt(data?.last_month, { compact: true })}</div>
    </div>
  </Panel>
);

const PendingApprovals = ({ data }) => {
  const items = data?.items || [];

  return (
    <Panel title={`Pending Approvals (${data?.count ?? 0})`}>
      {items.length === 0 ? (
        <Empty>No role requests waiting on you.</Empty>
      ) : (
        items.map((item) => (
          <div className="ld-row" key={item.id}>
            <div className="ld-row-main">
              <Avatar name={item.requester} />
              <div>
                <div className="ld-row-title">
                  {item.requester} → {item.role}
                </div>
                <div className="ld-row-sub">{item.step_name}</div>
              </div>
            </div>
            <Link to="/approver-dashboard" className="ld-link">
              Review
            </Link>
          </div>
        ))
      )}
      <div style={{ marginTop: 12 }}>
        <Link to="/approver-dashboard" className="ld-link">
          Open approver dashboard →
        </Link>
      </div>
    </Panel>
  );
};

const LargestOverdue = ({ data }) => (
  <Panel title="Largest Overdue Balances">
    {(data || []).length === 0 ? (
      <Empty>No overdue installments.</Empty>
    ) : (
      data.map((row) => (
        <div className="ld-row" key={row.id}>
          <div>
            <div className="ld-row-title">{row.customer}</div>
            <div className="ld-row-sub">
              {row.property || "Property"}
              {row.unit ? ` · ${row.unit}` : ""} · due {row.due_date}
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div className="ld-row-value bad">{formatBdt(row.amount)}</div>
            <span className="ld-badge">{row.days_overdue}d overdue</span>
          </div>
        </div>
      ))
    )}
  </Panel>
);

const Holdings = ({ data }) => {
  const items = data?.items || [];
  const upcoming = data?.upcoming || [];

  return (
    <Panel title="Property Holdings" wide>
      {items.length === 0 ? (
        <Empty>No property holdings yet.</Empty>
      ) : (
        <div className="ld-table-wrap">
          <table className="ld-table">
            <thead>
              <tr>
                <th>Property</th>
                <th>Unit</th>
                <th>Total</th>
                <th>Paid</th>
                <th>Due</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <td>
                    <strong>{item.property}</strong>
                  </td>
                  <td>{item.unit || "—"}</td>
                  <td>{formatBdt(item.total_amount)}</td>
                  <td>{formatBdt(item.paid_amount)}</td>
                  <td>{formatBdt(item.due_amount)}</td>
                  <td>
                    <span className={`ld-badge ${item.status === "sold" ? "ok" : "warn"}`}>{item.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {upcoming.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <h3 style={{ fontSize: 16, marginBottom: 8 }}>Upcoming Installments</h3>
          {upcoming.map((row) => (
            <div className="ld-row" key={row.id}>
              <span>
                {row.property || "Property"}
                {row.unit ? ` · ${row.unit}` : ""}
              </span>
              <span>
                {formatBdt(row.amount)} · {row.due_date}
              </span>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
};

const RecentPayments = ({ data }) => (
  <Panel title="Recent Payments">
    {(data || []).length === 0 ? (
      <Empty>No payments yet.</Empty>
    ) : (
      data.map((payment) => (
        <div className="ld-row" key={payment.id}>
          <div>
            <div className="ld-row-title">
              {payment.payment_type || "Payment"}
              {payment.property ? ` · ${payment.property}` : ""}
            </div>
            <div className="ld-row-sub">{payment.payment_date || "—"}</div>
          </div>
          <div className="ld-row-value">{formatBdt(payment.amount)}</div>
        </div>
      ))
    )}
  </Panel>
);

/** Charts, then lists, each in the order they appear on the page. `monthly_revenue` needs the month picker props. */
export const CHART_RENDERERS = {
  monthly_revenue: (data, extra) => <MonthlyRevenue key="monthly_revenue" data={data} {...extra} />,
  quarterly_trends: (data) => <QuarterlyTrends key="quarterly_trends" data={data} />,
  sales_by_area: (data) => <SalesByArea key="sales_by_area" data={data} />,
  inventory_by_type: (data) => <InventoryByType key="inventory_by_type" data={data} />,
  inventory_status: (data) => <InventoryStatus key="inventory_status" data={data} />,
};

export const LIST_RENDERERS = {
  revenue_by_property: (data) => <RevenueByProperty key="revenue_by_property" data={data} />,
  top_agents: (data) => <TopAgents key="top_agents" data={data} />,
  commission: (data) => <Commission key="commission" data={data} />,
  pending_approvals: (data) => <PendingApprovals key="pending_approvals" data={data} />,
  largest_overdue: (data) => <LargestOverdue key="largest_overdue" data={data} />,
  holdings: (data) => <Holdings key="holdings" data={data} />,
  recent_payments: (data) => <RecentPayments key="recent_payments" data={data} />,
};
